import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import PageTransition from './PageTransition';
import { useTransition } from '../contexts/TransitionContext';
import { pageOrder, allPages } from '../data/pageOrder';

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden; /* Pas de scroll ici, le scroll est géré par le body */
  -webkit-overflow-scrolling: touch;
`;

const CarouselWrapper = styled.div<{ $currentIndex: number; $shouldAnimate: boolean }>`
  position: relative;
  width: ${pageOrder.length * 100}%;
  height: 100%; /* Prendre la hauteur du conteneur parent */
  transform: translate3d(-${props => props.$currentIndex * (100 / pageOrder.length)}%, 0, 0);
  transition: ${props => props.$shouldAnimate ? 'transform 0.35s ease-out' : 'none'};
  pointer-events: ${props => props.$shouldAnimate ? 'none' : 'auto'};
  will-change: transform;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  
  @media (max-width: 768px) {
    transition: ${props => props.$shouldAnimate ? 'transform 0.25s ease-out' : 'none'};
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PageContainer = styled.div<{ $index: number; $isVisible: boolean }>`
  position: absolute;
  left: ${props => props.$index * (100 / pageOrder.length)}%;
  width: ${100 / pageOrder.length}%;
  height: ${props => props.$isVisible ? 'auto' : '0'}; /* Seule les pages visibles ont une hauteur */
  min-height: ${props => props.$isVisible ? 'fit-content' : '0'};
  overflow: hidden; /* Pas de scroll ici, géré par le body */
  backface-visibility: hidden;
  visibility: visible;
  opacity: 1;
`;

// Composant qui sépare le hero du contenu
const PageWithSeparatedContent: React.FC<{ 
  component: React.ComponentType; 
  path: string; 
  isAnimating: boolean;
}> = ({ component: Component, path, isAnimating }) => {
  // Rendre le hero immédiatement (header + hero)
  const heroElement = (
    <PageTransition>
      <Component />
    </PageTransition>
  );

  // Le contenu sera chargé progressivement via LazyPageContent dans SharedPageLayout
  // Pour l'instant, on rend tout mais SharedPageLayout gérera le lazy loading
  return heroElement;
};

// Composant de page mémorisé pour éviter les re-renders
const MemoizedPage: React.FC<{ component: React.ComponentType; path: string; isAnimating: boolean }> = React.memo(
  PageWithSeparatedContent,
  (prevProps, nextProps) => {
    // Ne jamais re-render pendant l'animation pour éviter les interruptions visuelles
    if (prevProps.isAnimating || nextProps.isAnimating) {
      return true; // Ne pas mettre à jour - garder l'ancien rendu
    }
    // Sinon, comparer normalement
    return prevProps.path === nextProps.path;
  }
);

MemoizedPage.displayName = 'MemoizedPage';

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();
  const initialIndex = allPages.findIndex(page => page.path === location.pathname);
  const [currentIndex, setCurrentIndex] = useState(initialIndex !== -1 ? initialIndex : 0);
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([initialIndex !== -1 ? initialIndex : 0]));
  const { shouldAnimate, setShouldAnimate } = useTransition();
  const carouselRef = useRef<HTMLDivElement>(null);
  const previousIndexRef = useRef<number>(initialIndex !== -1 ? initialIndex : 0);
  const isAnimatingRef = useRef<boolean>(false); // Flag pour bloquer les changements pendant l'animation

  // Précharger toutes les pages au montage - forcer le rendu complet
  useEffect(() => {
    // Forcer le rendu initial de toutes les pages immédiatement
    // Les pages sont déjà dans le DOM, on veut juste s'assurer qu'elles sont complètement rendues
    const preloadPages = () => {
      // Forcer un reflow pour s'assurer que toutes les pages sont calculées et mises en cache
      if (carouselRef.current) {
        // Lire les dimensions pour forcer le layout et le rendu complet
        void carouselRef.current.offsetHeight; // Force reflow
        void carouselRef.current.offsetWidth; // Force reflow
        // Forcer le rendu de toutes les pages enfants
        const containers = carouselRef.current.querySelectorAll('[data-page-container]');
        containers.forEach((container) => {
          void (container as HTMLElement).offsetHeight; // Force reflow pour chaque page
        });
      }
    };

    // Commencer le préchargement après le premier rendu
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        preloadPages();
      });
    });
  }, []);

  // Désactiver le scroll pendant l'animation
  useEffect(() => {
    if (shouldAnimate) {
      // Empêcher le scroll pendant l'animation
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      };
    } else {
      // Si shouldAnimate devient false, réinitialiser le flag d'animation après un court délai
      // pour s'assurer que l'animation CSS est terminée
      const resetTimer = setTimeout(() => {
        isAnimatingRef.current = false;
      }, 100);
      
      return () => {
        clearTimeout(resetTimer);
      };
    }
  }, [shouldAnimate]);

  // Trouver l'index de la page actuelle et gérer le scroll
  useEffect(() => {
    // Ignorer les changements pendant qu'une animation est en cours
    if (isAnimatingRef.current) {
      return;
    }
    
    const index = allPages.findIndex(page => page.path === location.pathname);
    // Ignorer si c'est la même page ou si l'animation est en cours
    if (index !== -1 && index !== previousIndexRef.current && !isAnimatingRef.current) {
      if (shouldAnimate) {
        // Marquer qu'une animation est en cours pour bloquer les changements
        isAnimatingRef.current = true;
        
        // Pour les animations: s'assurer que la page cible est complètement rendue
        // 1. Scroll vers le haut immédiatement
        window.scrollTo({ top: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        
        // 2. Rendre toutes les pages intermédiaires visibles AVANT l'animation pour éviter le blanc
        const startIndex = Math.min(previousIndexRef.current, index);
        const endIndex = Math.max(previousIndexRef.current, index);
        const intermediatePages = new Set<number>();
        for (let i = startIndex; i <= endIndex; i++) {
          intermediatePages.add(i);
        }
        setVisiblePages(intermediatePages);
        
        // 3. Attendre que la page cible soit complètement rendue avant de commencer l'animation
        // Toutes les pages sont déjà dans le DOM, on doit juste s'assurer qu'elles sont calculées
        const startAnimation = () => {
          // Vérifier que la page cible existe dans le DOM
          const containers = carouselRef.current?.querySelectorAll('[data-page-container]');
          const targetContainer = containers?.[index] as HTMLElement;
          const previousContainer = containers?.[previousIndexRef.current] as HTMLElement;
          
          if (targetContainer && containers && containers.length > index) {
            // Forcer un reflow complet pour s'assurer que la page est complètement rendue et calculée
            // Lire plusieurs propriétés pour forcer le layout
            void targetContainer.offsetHeight;
            void targetContainer.offsetWidth;
            void targetContainer.scrollHeight;
            
            // Mettre à jour la hauteur du wrapper AVANT l'animation avec la hauteur de la page cible
            if (carouselRef.current) {
              const targetHeight = targetContainer.scrollHeight;
              carouselRef.current.style.height = `${targetHeight}px`;
            }
            
            // Forcer aussi un reflow sur le wrapper pour s'assurer que le transform est calculé
            if (carouselRef.current) {
              void carouselRef.current.offsetHeight;
            }
            
            // Attendre encore un frame pour être sûr que tout est prêt et mis en cache GPU
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // Maintenant on peut démarrer l'animation en toute sécurité
                setCurrentIndex(index);
                previousIndexRef.current = index;
              });
            });
          } else {
            // Si la page n'est pas encore dans le DOM (ne devrait pas arriver), attendre
            setTimeout(() => {
              startAnimation();
            }, 16);
          }
        };
        
        // Commencer après quelques frames pour laisser React finir de rendre et le navigateur de calculer
        const rafId = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            startAnimation();
          });
        });
        
        // 4. Réinitialiser l'animation après le glissement (durée réduite sur mobile)
        const isMobile = window.innerWidth <= 768;
        const animationDuration = isMobile ? 250 : 350;
        const animationTimer = setTimeout(() => {
          // Attendre que l'animation CSS soit complètement terminée avant de réinitialiser
          setShouldAnimate(false);
          // Cacher l'ancienne page après l'animation
          setVisiblePages(new Set([index]));
          // Réinitialiser le flag après un petit délai supplémentaire pour être sûr
          setTimeout(() => {
            isAnimatingRef.current = false;
          }, 50);
        }, animationDuration + 50); // Ajouter un petit buffer
        
        return () => {
          cancelAnimationFrame(rafId);
          clearTimeout(animationTimer);
          // Réinitialiser le flag si le composant est démonté ou l'effet annulé
          isAnimatingRef.current = false;
        };
      } else {
        // Pour les navigations normales: changement instantané + scroll immédiat
        setVisiblePages(new Set([index]));
        requestAnimationFrame(() => {
          setCurrentIndex(index);
          previousIndexRef.current = index;
          window.scrollTo({ top: 0, behavior: 'auto' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        });
      }
    }
  }, [location.pathname, shouldAnimate, setShouldAnimate]);

  // Mettre à jour la hauteur du wrapper en fonction de la page visible (seulement si pas d'animation)
  useEffect(() => {
    if (shouldAnimate || isAnimatingRef.current) {
      return; // Ne pas mettre à jour pendant l'animation
    }

    const updateHeight = () => {
      if (carouselRef.current) {
        const containers = carouselRef.current.querySelectorAll('[data-page-container]');
        const currentContainer = containers[currentIndex] as HTMLElement;
        
        if (currentContainer) {
          requestAnimationFrame(() => {
            const pageHeight = currentContainer.scrollHeight;
            if (carouselRef.current) {
              carouselRef.current.style.height = `${pageHeight}px`;
            }
          });
        }
      }
    };

    updateHeight();
    const timeoutId = setTimeout(updateHeight, 100);
    
    if (carouselRef.current && 'ResizeObserver' in window) {
      const containers = carouselRef.current.querySelectorAll('[data-page-container]');
      const currentContainer = containers[currentIndex] as HTMLElement;
      
      if (currentContainer) {
        const resizeObserver = new ResizeObserver(() => {
          if (!shouldAnimate && !isAnimatingRef.current) {
            updateHeight();
          }
        });
        resizeObserver.observe(currentContainer);
        
        return () => {
          resizeObserver.disconnect();
          clearTimeout(timeoutId);
        };
      }
    }
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentIndex, shouldAnimate]);

  // Mémoriser le rendu des pages pour éviter les re-renders pendant l'animation
  const renderedPages = useMemo(() => {
    return allPages.map((page, index) => (
      <PageContainer 
        key={page.path} 
        $index={index} 
        $isVisible={visiblePages.has(index)}
        data-page-container
      >
        <MemoizedPage 
          component={page.component} 
          path={page.path} 
          isAnimating={shouldAnimate || isAnimatingRef.current}
        />
      </PageContainer>
    ));
  }, [shouldAnimate, visiblePages]); // Re-render si les pages visibles changent

  return (
    <CarouselContainer>
      <CarouselWrapper ref={carouselRef} $currentIndex={currentIndex} $shouldAnimate={shouldAnimate}>
        {renderedPages}
      </CarouselWrapper>
    </CarouselContainer>
  );
};

export default AnimatedRoutes;
